import { Mail, AlertCircle, CheckCircle } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const VerifyEmailRequiredPage = () => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  
  const { user, resendEmailVerification, logout } = useAuth()
  const navigate = useNavigate()

  const handleResendEmail = async () => {
    setError('')
    setLoading(true)
    try {
      await resendEmailVerification()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 5000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha ao reenviar email. Tente novamente.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  return (
    <div className="flex items-center justify-center p-4 flex-grow">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-noe-brand animate-pulse" />
            <span className="text-xl font-bold tracking-wide">NOÉ CONNECT</span>
          </div>
          <h1 className="text-4xl font-bold font-serif tracking-tight mt-4">
            Verificação de e-mail necessária
          </h1>
          <p className="text-noe-400 mt-2">
            Para acessar a plataforma, verifique seu endereço de e-mail
          </p>
        </div>

        {/* Content card */}
        <div className="bg-noe-900/30 border border-noe-800 rounded-[2rem] p-8 backdrop-blur-xl">
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3 text-yellow-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm text-gray-900">Verifique seu e-mail para acessar todos os recursos</span>
          </div>

          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-noe-brand/10 rounded-full mb-6">
              <Mail className="w-8 h-8 text-noe-brand" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Verifique seu e-mail</h3>
            <p className="text-noe-400 mb-4">
              Enviamos um link de verificação para <strong>{user?.email}</strong>
            </p>
            <p className="text-sm text-noe-500 mb-6">
              Clique no link no e-mail para confirmar sua conta. 
              Se não encontrar o e-mail, verifique sua pasta de spam.
            </p>

            {success && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3 text-green-300">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">E-mail de verificação reenviado com sucesso!</span>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <button
                onClick={handleResendEmail}
                disabled={loading}
                className="w-full py-3 px-6 bg-noe-brand text-white font-medium rounded-xl hover:bg-noe-brandHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Reenviar e-mail de verificação
                  </>
                )}
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 px-6 bg-noe-800 text-noe-300 font-medium rounded-xl hover:bg-noe-700 transition-colors"
              >
                Voltar para o login
              </button>

              <button
                onClick={handleLogout}
                className="w-full py-3 px-6 bg-transparent text-noe-500 font-medium rounded-xl hover:bg-noe-800/30 hover:text-noe-300 transition-colors"
              >
                Sair da conta
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-noe-800">
              <p className="text-sm text-noe-500">
                Precisa de ajuda?{' '}
                <a href="mailto:support@noeconnect.com" className="text-noe-brand hover:text-noe-brandHover font-medium">
                  Entre em contato com o suporte
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-6 text-noe-600 text-sm">
            <span>Registro seguro</span>
            <span>•</span>
            <span>Conexão criptografada</span>
            <span>•</span>
            <span>Conformidade GDPR</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailRequiredPage